import { useState, useEffect } from 'react';
import { formatINR } from '../utils/grnHelpers';
import { type MockGRN } from '@/mocks/billing';
import { asEntered } from '@/hooks/useUnits';

interface PrintGRNProps {
  grn: MockGRN;
  onClose: () => void;
}

interface Company {
  id: string;
  name: string;
  address: string;
  gstin: string;
  pan: string;
  phone: string;
  email: string;
  logo_url?: string | null;
}

function PrintGRNView({ grn, onClose }: PrintGRNProps) {
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  const totalQty = grn.items.reduce((s, i) => s + i.qty, 0);
  const totalAmt = grn.items.reduce((s, i) => s + i.qty * i.rate, 0);

  // ✅ Fetch company details
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/company/get`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        const result = await response.json();
        if (result?.success && result?.data) {
          setCompany(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch company:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  // ✅ Default fallback values
  const c = {
    name: company?.name || 'Company Name',
    address: company?.address || '',
    gstin: company?.gstin || '',
    phone: company?.phone || '',
    email: company?.email || '',
    pan: company?.pan || '',
    logo_url: company?.logo_url || null,
  };

  // ✅ Logo handling
  const companyLogo = c.logo_url || null;
  let logoUrl = null;
  if (companyLogo) {
    if (companyLogo.startsWith('data:image')) {
      logoUrl = companyLogo;
    } else {
      logoUrl = `${import.meta.env.VITE_API_URL}${companyLogo}`;
    }
  }

  const handlePrint = () => window.print();

  // ✅ Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 flex items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-[#4f46e5] text-2xl" />
          <span className="text-sm text-slate-600">
            Loading company details...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[60] flex items-start justify-center overflow-y-auto py-8"
      onClick={onClose}
    >
      <div
        className="bg-white w-[210mm] shadow-2xl rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 bg-[#1e293b] text-white print:hidden">
          <div className="flex items-center gap-2">
            <i className="ri-inbox-archive-line text-indigo-300" />
            <span className="text-sm font-medium">
              GRN Preview — {grn.grnNumber}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 h-7 px-3 rounded text-xs font-medium bg-[#4f46e5] hover:bg-indigo-600 text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-printer-line" />
              Print
            </button>
            <button
              onClick={onClose}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/10 cursor-pointer text-slate-300"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        {/* A4 Content */}
        <div className="p-8 print-area text-[11px]">
          {/* Company header with Logo */}
          <div className="flex justify-between items-start mb-5 pb-4 border-b-2 border-[#e2e8f0]">
            <div className="flex items-start gap-4">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={c.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#4f46e5] bg-white shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-2xl font-bold text-[#4f46e5] border-2 border-[#4f46e5] shrink-0">
                  {c.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="text-lg font-bold text-[#1e293b]">{c.name}</h1>
                <p className="text-[#64748b] mt-1">{c.address}</p>
                <p className="mt-1">
                  <strong>GSTIN:</strong> {c.gstin} &nbsp;|&nbsp;{' '}
                  <strong>PAN:</strong> {c.pan}
                </p>
                <p>
                  <strong>Ph:</strong> {c.phone} &nbsp;|&nbsp;{' '}
                  <strong>Email:</strong> {c.email}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl font-extrabold text-[#4f46e5]">
                GOODS RECEIPT NOTE
              </p>
              <div className="mt-2 border border-[#e2e8f0] rounded-lg p-3 text-left inline-block">
                <p className="text-[10px] text-[#94a3b8] uppercase">GRN No</p>
                <p className="font-bold">{grn.grnNumber}</p>
                <p className="text-[10px] text-[#94a3b8] uppercase mt-2">
                  Date
                </p>
                <p>{grn.date}</p>
                <p className="text-[10px] text-[#94a3b8] uppercase mt-2">
                  Warehouse
                </p>
                <p>{grn.warehouseName}</p>
              </div>
            </div>
          </div>

          {/* Supplier info */}
          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-[#f8fafc] rounded-lg">
            <div>
              <p className="text-[10px] text-[#94a3b8] uppercase font-semibold mb-1">
                Received From (Supplier)
              </p>
              <p className="font-bold text-sm">{grn.supplierName}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#94a3b8] uppercase font-semibold mb-1">
                Received At
              </p>
              <p className="font-bold text-sm">{grn.warehouseName}</p>
              <p className="text-[#64748b] mt-0.5">
                Created by: {grn.createdBy}
              </p>
            </div>
          </div>

          {/* Linked POs */}
          {grn.linkedPOs.length > 0 && (
            <div className="mb-4 p-2 bg-emerald-50 border border-emerald-200 rounded-lg text-[10px]">
              <strong className="text-emerald-700">Linked POs: </strong>
              <span className="text-emerald-800">
                {grn.linkedPOs.join(', ')}
              </span>
            </div>
          )}

          {/* Items table */}
          <table className="w-full border-collapse border border-[#e2e8f0] text-[10px] mb-4">
            <thead>
              <tr className="bg-[#f8fafc]">
                {[
                  'Sr',
                  'Item Description',
                  'Batch No',
                  'HSN',
                  'Qty',
                  'Unit',
                  'Rate (₹)',
                  'Amount (₹)',
                  'PO Ref',
                ].map((h, i) => (
                  <th
                    key={h}
                    className={`border border-[#e2e8f0] px-2 py-1.5 font-semibold text-[#64748b] uppercase ${
                      ['Qty', 'Rate (₹)', 'Amount (₹)'].includes(h)
                        ? 'text-right'
                        : 'text-left'
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grn.items.map((item, i) => (
                <tr key={i} className={i % 2 === 1 ? 'bg-[#fafafa]' : ''}>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-center">
                    {i + 1}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 font-medium">
                    {item.itemName}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 font-mono text-[9px]">
                    <div>{item.batchNumber || '—'}</div>
                    {(item.mfgDate || item.expiryDate) && (
                      <div className="text-[8px] text-[#64748b]">
                        {item.mfgDate ? `M:${item.mfgDate.slice(0, 10)}` : ''}
                        {item.expiryDate ? ` E:${item.expiryDate.slice(0, 10)}` : ''}
                      </div>
                    )}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5">
                    {item.hsnCode}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    {asEntered(item).qty}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5">
                    {asEntered(item).unit}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                    {formatINR(asEntered(item).rate)}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-right font-bold">
                    {formatINR(item.qty * item.rate)}
                  </td>
                  <td className="border border-[#e2e8f0] px-2 py-1.5 text-[9px]">
                    {item.poRef ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#f8fafc] font-bold">
                <td
                  colSpan={4}
                  className="border border-[#e2e8f0] px-2 py-1.5 text-right"
                >
                  Total
                </td>
                <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                  {totalQty}
                </td>
                <td className="border border-[#e2e8f0] px-2 py-1.5"></td>
                <td className="border border-[#e2e8f0] px-2 py-1.5"></td>
                <td className="border border-[#e2e8f0] px-2 py-1.5 text-right">
                  {formatINR(totalAmt)}
                </td>
                <td className="border border-[#e2e8f0] px-2 py-1.5"></td>
              </tr>
            </tfoot>
          </table>

          {/* Signatures */}
          <div className="grid grid-cols-3 gap-4 mt-8 pt-4 border-t border-[#e2e8f0]">
            <div className="text-center text-[10px] text-[#64748b]">
              <p>Received By</p>
              <p className="mt-8 border-t border-[#e2e8f0] pt-1">
                {grn.createdBy}
              </p>
            </div>
            <div className="text-center text-[10px] text-[#64748b]">
              <p>Checked By</p>
              <p className="mt-8 border-t border-[#e2e8f0] pt-1">
                ________________
              </p>
            </div>
            <div className="text-center text-[10px] text-[#64748b]">
              <p className="font-semibold text-[#1e293b]">{c.name}</p>
              <p className="mt-8 border-t border-[#e2e8f0] pt-1">
                Authorised Signatory
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrintGRNView;
